import React, { useEffect, useState } from 'react';
import EmailsPanel from './EmailsPanel';
import FeedbackPanel from './FeedbackPanel';
import LookupPanel from './LookupPanel';
import MatchesPanel from './MatchesPanel';
import ReportsPanel from './ReportsPanel';
import UserPreview from './UserPreview';
import VerificationPanel from './VerificationPanel';
import { adminCountKonjoMatches, callableErrorMessage, reviewFeedback, reviewNewUsers, reviewOldUsers, reviewReports } from '../../../adminApi';

function AdminDesk(props) {
  const [tab, setTab] = useState('verification');
  const [queue, setQueue] = useState('new');
  const [stats, setStats] = useState({ newCount: 0, oldCount: 0, reportCount: 0, emailCount: 0, feedbackCount: 0, matchCount: 0 });
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const loadStats = () => {
    Promise.all([reviewNewUsers(), reviewOldUsers(), reviewReports(), reviewFeedback(), adminCountKonjoMatches()])
      .then(([newUsers, oldUsers, reports, feedback, matches]) => {
        setStats((current) => ({
          newCount: (newUsers || []).length,
          oldCount: (oldUsers || []).length,
          reportCount: (reports || []).length,
          emailCount: current.emailCount,
          feedbackCount: (feedback || []).length,
          matchCount: matches && typeof matches.count === 'number' ? matches.count : 0
        }));
      })
      .catch((err) => setError(callableErrorMessage(err)));
  };

  useEffect(() => {
    loadStats();
  }, []);

  const openStat = (nextTab, nextQueue) => {
    setTab(nextTab);
    if (nextQueue) {
      setQueue(nextQueue);
    }
  };

  const syncPreview = (updatedUsers) => {
    setPreview((current) => {
      if (!current) {
        return current;
      }
      if (Array.isArray(updatedUsers)) {
        const match = updatedUsers.find((user) => user && user.userId === current.userId);
        return match || current;
      }
      return (updatedUsers && updatedUsers[current.userId]) || current;
    });
  };

  return (
    <main className="admin-desk">
      <div className="admin-toolbar">
        <div>
          <h1>Admin</h1>
          <p className="admin-muted">Verification, reports, emails, feedback, account search, and Konjo AI matches.</p>
        </div>
      </div>
      {error ? <p className="admin-error">{error}</p> : null}
      <div className="admin-stats">
        <button
          type="button"
          className={tab === 'matches' ? 'admin-stat is-active' : 'admin-stat'}
          onClick={() => openStat('matches')}
        >
          <strong>{stats.matchCount}</strong>
          <span>Konjo AI Matches</span>
        </button>
        <button
          type="button"
          className={tab === 'verification' && queue === 'new' ? 'admin-stat is-active' : 'admin-stat'}
          onClick={() => openStat('verification', 'new')}
        >
          <strong>{stats.newCount}</strong>
          <span>New verification</span>
        </button>
        <button
          type="button"
          className={tab === 'verification' && queue === 'old' ? 'admin-stat is-active' : 'admin-stat'}
          onClick={() => openStat('verification', 'old')}
        >
          <strong>{stats.oldCount}</strong>
          <span>Re-review</span>
        </button>
        <button
          type="button"
          className={tab === 'reports' ? 'admin-stat is-active' : 'admin-stat'}
          onClick={() => openStat('reports')}
        >
          <strong>{stats.reportCount}</strong>
          <span>Pending reports</span>
        </button>
        <button
          type="button"
          className={tab === 'emails' ? 'admin-stat is-active' : 'admin-stat'}
          onClick={() => openStat('emails')}
        >
          <strong>{stats.emailCount}</strong>
          <span>Banned emails</span>
        </button>
        <button
          type="button"
          className={tab === 'feedback' ? 'admin-stat is-active' : 'admin-stat'}
          onClick={() => openStat('feedback')}
        >
          <strong>{stats.feedbackCount}</strong>
          <span>Unread feedback</span>
        </button>
      </div>
      <nav className="admin-tabs" aria-label="Admin sections">
        <button type="button" className={tab === 'verification' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => openStat('verification', 'new')}>
          Verification
        </button>
        <button type="button" className={tab === 'reports' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => setTab('reports')}>
          Inbox
        </button>
        <button type="button" className={tab === 'emails' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => setTab('emails')}>
          Emails
        </button>
        <button type="button" className={tab === 'feedback' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => setTab('feedback')}>
          Feedback
        </button>
        <button type="button" className={tab === 'matches' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => setTab('matches')}>
          Konjo AI Matches
        </button>
        <button type="button" className={tab === 'lookup' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => setTab('lookup')}>
          Look up
        </button>
      </nav>
      {tab === 'verification' ? (
        <VerificationPanel
          queue={queue}
          onPreview={setPreview}
          onUsersUpdated={syncPreview}
          onChanged={loadStats}
        />
      ) : null}
      {tab === 'reports' ? (
        <ReportsPanel onPreview={setPreview} onUsersUpdated={syncPreview} onChanged={loadStats} />
      ) : null}
      {tab === 'emails' ? (
        <EmailsPanel
          onPreview={setPreview}
          onUsersUpdated={syncPreview}
          onChanged={(count) => {
            if (typeof count === 'number') {
              setStats((current) => Object.assign({}, current, { emailCount: count }));
            }
          }}
        />
      ) : null}
      {tab === 'feedback' ? (
        <FeedbackPanel
          onPreview={setPreview}
          onUsersUpdated={syncPreview}
          onChanged={(count) => {
            if (typeof count === 'number') {
              setStats((current) => Object.assign({}, current, { feedbackCount: count }));
            } else {
              loadStats();
            }
          }}
        />
      ) : null}
      {tab === 'lookup' ? (
        <LookupPanel onPreview={setPreview} onUsersUpdated={syncPreview} onChanged={loadStats} />
      ) : null}
      {tab === 'matches' ? (
        <MatchesPanel
          onPreview={setPreview}
          onCount={(count) => {
            if (typeof count === 'number') {
              setStats((current) => Object.assign({}, current, { matchCount: count }));
            }
          }}
          onChanged={loadStats}
        />
      ) : null}
      {preview ? <UserPreview user={preview} onClose={() => setPreview(null)} /> : null}
    </main>
  );
}

export default AdminDesk;
